import * as React from "react"
import Subtitle from "../../../atoms/typography/Subtitle"
import { HorizontalSpacer } from "../../../atoms/spacers"
import { Col, Row, Visible } from "react-grid-system"
import TextInput from "../../TextInput"
import AddressValidationRules from "../../../atoms/inputs/AddressValidationRules"
import Select from "../../Select"
import { useAcceptedCountries } from "../../../../hooks/useAcceptedCountries"
import { CustomWindow } from "../../../../custom"
import { checkEmailValid } from "../../../../utils/helpers"
import * as Sentry from "@sentry/react"
import { User } from "../../../interfaces/SaleorTypes"
import { BillingInfo } from "../../../pages/products/BookRental"

interface Props {
  formData: BillingInfo
  setFormData: any
  missingFields: Array<string>
}

const BillingAddressForm: React.FC<Props> = ({ formData, setFormData, missingFields }) => {
  const { countries, error, loading } = useAcceptedCountries()

  const handleChangeBillingAddressForUnregisteredUser = (event: any) => {
    const name = event.target.name
    const value = event.target.value
    setFormData((values: any) => ({ ...values, [name]: value }))
  }

  // Update the button state whenever form values change
  const handleFormChange = (event: any) => {
    handleChangeBillingAddressForUnregisteredUser(event)
  }

  return (
    <div>
      <Subtitle>Billing address</Subtitle>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="First name"
            type="text"
            name="bFirstName"
            id="bFirstName"
            defaultValue={formData.bFirstName}
            required
            onChange={handleFormChange}
            errorMessage={missingFields.includes("bFirstName") ? "This field can't be empty" : undefined}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Last name"
            type="text"
            name="bLastName"
            id="bLastName"
            defaultValue={formData.bLastName}
            required
            onChange={handleFormChange}
            errorMessage={missingFields.includes("bLastName") ? "This field can't be empty" : undefined}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="Email"
            type="text"
            name="bEmail"
            id="bEmail"
            onChange={handleFormChange}
            defaultValue={formData.bEmail}
            required
            inputAccepted={checkEmailValid(formData.bEmail)}
            errorMessage={missingFields.includes("bEmail") ? "This field can't be empty" : undefined}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Mobile Phone"
            type="tel"
            name="bPhone"
            id="bPhone"
            onChange={handleFormChange}
            defaultValue={formData.bPhone}
            required
            errorMessage={missingFields.includes("bPhone") ? "This field can't be empty" : undefined}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="Billing address 1"
            type="text"
            name="bStreetAdress1"
            id="bStreetAdress1"
            defaultValue={formData.bStreetAdress1}
            onChange={handleFormChange}
            required
            errorMessage={missingFields.includes("bStreetAdress1") ? "This field can't be empty" : undefined}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Billing address 2 (optional)"
            type="text"
            name="bStreetAdress2"
            id="bStreetAdress2"
            onChange={handleFormChange}
            defaultValue={formData.bStreetAdress2}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <TextInput
            textFieldTitle="City"
            type="text"
            name="bCity"
            id="bCity"
            onChange={handleFormChange}
            required
            defaultValue={formData.bCity}
            errorMessage={missingFields.includes("bCity") ? "This field can't be empty" : undefined}
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Postal code"
            type="text"
            name="bPostalCode"
            id="bPostalCode"
            onChange={handleFormChange}
            required
            defaultValue={formData.bPostalCode}
            errorMessage={missingFields.includes("bPostalCode") ? "This field can't be empty" : undefined}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />

      <Row justify="between" align="center">
        <Col md={6} xs={12}>
          <Select
            title="Country"
            options={countries ?? []}
            id="bCountry"
            name="bCountry"
            onChange={handleFormChange}
            defaultValue={formData.bCountry}
            required
          />
        </Col>
        <Col md={6} xs={12}>
          <Visible xs>
            <HorizontalSpacer spacing={4} />
          </Visible>
          <TextInput
            textFieldTitle="Company"
            type="text"
            name="bCompany"
            id="bCompany"
            onChange={handleFormChange}
            defaultValue={formData.bCompany}
            required
            errorMessage={missingFields.includes("bCompany") ? "This field can't be empty" : undefined}
          />
        </Col>
        {formData.bCountry && (
          <Col md={6} xs={12}>
            <AddressValidationRules
              countryCode={formData.bCountry}
              name="bCountryArea"
              id="bCountryArea"
              onChange={handleFormChange}
            />
          </Col>
        )}
      </Row>

      <div></div>
    </div>
  )
}

export default BillingAddressForm
